import React from 'react';
import { ArrowRightIcon, EyeIcon, GlassesIcon, InfoIcon, PlusIcon, ShoppingCartIcon, SquareStackIcon } from 'lucide-react';
import { RxMark } from './PrescriptionBadge';
import { lenses, media } from '../data/product';
import { builderAssets } from '../data/builder';
import { deepLinkClick } from '../utils/deepLinks';
import { trackCustom } from '../utils/tracking';
const steps = [{
  icon: ShoppingCartIcon,
  text: 'Add the RX insert to your X1 order (+$14.95).'
}, {
  icon: GlassesIcon,
  text: 'Take it to your optical professional to fit your prescription lenses.'
}, {
  icon: SquareStackIcon,
  text: 'Snap it in behind the outer lens. Swap outer lenses anytime.'
}];

/** #rx · the RX Capability Insert, available now as a $14.95 add-on. */
export function PrescriptionSection() {
  return <section id="rx" aria-labelledby="rx-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" data-id="element-1080" data-mp="as78bth852ff">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-1081" data-mp="fs3bjqi3kptx">
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-14" data-id="element-1082" data-mp="2f79f2cujtg9x">
          <div data-id="element-1083" data-mp="2b6h2b9mgwt8w">
            <div className="flex items-center gap-3" data-id="element-1084" data-mp="2b6z8axyxmlu0">
              <RxMark className="h-10 w-10 shrink-0" data-id="element-1085" data-mp="2b8aet5jri7ot" />
              <p className="font-display text-xl font-extrabold uppercase tracking-wide text-[#7fe3ec]" data-id="element-1086" data-mp="2p7080a3gnn7w">Prescription ready</p>
            </div>
            <h2 id="rx-heading" className="grit-headline mt-4 text-[clamp(2.25rem,7vw,4.25rem)] font-extrabold uppercase text-offwhite" data-id="element-1087" data-mp="2k8ys1y1c040n">
              Wear prescription lenses? <span className="text-grit-500" data-id="element-1088" data-mp="2u4a6ef8tv97v">Your X1 is ready.</span>
            </h2>
            <p className="mt-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-1089" data-mp="37wq5x62isp41">
              The GritVue RX Capability Insert™. Available now for $14.95.
            </p>
            <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-steel-300" data-id="element-1090" data-mp="3htusv846wolw">
              A small removable insert that sits behind the X1&apos;s outer lens, closer to your eyes. Your optical
              professional fits your prescription lenses into the insert. Then you swap Clear, Amber and Smoke outer
              lenses anytime while your prescription stays in place.
            </p>

            <ol className="mt-8 grid gap-px bg-white/10 border border-white/10" data-id="element-1091" data-mp="3twnq2aqtst56">
              {steps.map(({
              icon: Icon,
              text
            }, i) => <li key={text} className="flex items-start gap-4 bg-ink-900 p-4 sm:p-5" data-id="element-1092" data-mp="2kcilgpgzlsx" data-mpi={i}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-grit-500 text-ink-950" data-id="element-1093" data-mp="7h2x3zlmm9c8">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-1094" data-mp="bl0w8aaybk5a" />
                  </span>
                  <p className="pt-1.5 text-[15px] font-medium leading-snug text-offwhite" data-id="element-1095" data-mp="giu4r9zq8q8q">
                    <span className="mr-2 font-display text-lg font-extrabold text-grit-500" data-id="element-1096" data-mp="lgcdaz9u13g7">{i + 1}.</span>
                    {text}
                  </p>
                </li>)}
            </ol>

            <p className="mt-5 flex items-start gap-3 border border-[#7fe3ec]/35 bg-[#07242a]/70 p-4 text-[14px] leading-relaxed text-steel-200" data-id="element-1097" data-mp="2nal3ac2ju5ja">
              <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#7fe3ec]" aria-hidden="true" data-id="element-1098" data-mp="2axzhd72n1qhu" />
              <span data-id="element-1099" data-mp="25ymsyhw4cbd5">
                Insert only. Prescription lenses are not included and are made by your optical professional.
                Compatibility depends on your prescription; ask your optical professional.
              </span>
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-1100" data-mp="2ylr311a2jfvs">
              <a href="#build" onClick={event => {
              trackCustom('rx_section_clicked', {
                target: 'build'
              });
              deepLinkClick('build', {
                builderStep: 'rx'
              })(event);
            }} className="inline-flex items-center justify-center gap-2 bg-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1101" data-mp="2tq21wtiehqxd">
                Add RX to my build
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1102" data-mp="2n7ffttcutvjc" />
              </a>
              <a href="#trio" onClick={event => {
              trackCustom('rx_section_clicked', {
                target: 'trio'
              });
              deepLinkClick('trio')(event);
            }} className="inline-flex items-center justify-center gap-2 border-2 border-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-grit-500 transition-[background-color,color,transform] duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1103" data-mp="2n5ksy9i0vtcm">
                Add RX to a pack
              </a>
            </div>
          </div>

          {/* Exploded view: the X1, its outer lens, and the RX insert that sits behind it */}
          <figure className="border border-white/10 bg-black" data-id="element-1104" data-mp="hm184edbiig1">
            <img src={builderAssets.x1ByLens.smoke} alt="GritVue X1 illuminated work glasses with the Smoke outer lens" className="block aspect-[4/3] w-full object-contain" loading="lazy" data-id="element-1105" data-mp="ckbv4t6jbupb" />
            <div className="grid grid-cols-[1fr_auto_1fr_auto_auto] items-center gap-2 border-t border-white/10 p-4 sm:gap-4 sm:p-6" data-id="element-1106" data-mp="3uyutx1wocsn5">
              <div className="text-center" data-id="element-1107" data-mp="3q1b5582coxgy">
                <div className="mx-auto flex h-20 items-center justify-center gap-1 sm:h-24" data-id="element-1108" data-mp="3py6txqqgx1gs">
                  {lenses.map(lens => <span key={lens.name} className="h-10 w-7 rounded-[999px_999px_40px_40px] border border-white/30 sm:h-12 sm:w-9" style={{
                  backgroundColor: lens.swatch
                }} aria-hidden="true" data-id="element-1109" data-mp="2v1zj3chftwg" />)}
                </div>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.14em] text-offwhite" data-id="element-1110" data-mp="1a6td28t78tay">Outer lens</p>
                <p className="text-[11px] text-steel-400" data-id="element-1111" data-mp="1f5mg17av11wv">Clear · Amber · Smoke</p>
              </div>
              <PlusIcon className="h-4 w-4 text-steel-500" aria-hidden="true" data-id="element-1112" data-mp="1oxshr8qwyxp9" />
              <div className="text-center" data-id="element-1113" data-mp="1ozv51l0omcqa">
                <div className="mx-auto h-20 w-full max-w-[120px] sm:h-24" data-id="element-1114" data-mp="rajnopcukgbn">
                  <img src={media.rxInsert} alt="GritVue RX Capability Insert" className="h-full w-full object-contain" loading="lazy" data-id="element-1115" data-mp="w94s70z4tm5w" />
                </div>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-1116" data-mp="168vnh4vio571">RX insert</p>
                <p className="text-[11px] text-steel-400" data-id="element-1117" data-mp="2cn3co9ll1xvh">Your prescription</p>
              </div>
              <ArrowRightIcon className="h-4 w-4 text-steel-500" aria-hidden="true" data-id="element-1118" data-mp="2hkmpd52mu2lb" />
              <div className="text-center" data-id="element-1119" data-mp="5yktuw4gnkrl">
                <div className="mx-auto flex h-20 w-12 items-center justify-center sm:h-24" data-id="element-1120" data-mp="1018oxxoyd0a">
                  <EyeIcon className="h-8 w-8 text-[#7fe3ec]" aria-hidden="true" data-id="element-1121" data-mp="3sep2ehbu8gve" />
                </div>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.14em] text-offwhite" data-id="element-1122" data-mp="3nflyj07lggp0">You</p>
              </div>
            </div>
            <figcaption className="border-t border-white/10 px-4 py-3 text-[12px] text-steel-400 sm:px-6" data-id="element-1123" data-mp="3ihamcitttu3m">
              Front to back: outer lens, then the RX insert, closer to your eyes. One pair of glasses.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>;
}